<script setup lang="ts">
  import { type Photoable, type Photo, createPhotosFromFilename, extractFromPhotoable, parseSize, photoUrl, mediaQuery } from '@storefront/resources';
  import { computed } from 'vue';

  interface Props {
    model?: Photoable | null | undefined,
    photo?: Photo | Photo[] | string | null | undefined,
    type?: string | null | undefined,
    sizes?: string | null | undefined,
    alt?: string | null | undefined,
    loading?: 'eager' | 'lazy' | null | undefined,
    fallback?: string | null | undefined,
    multi?: boolean | null | undefined,
  }

  defineOptions({
    inheritAttrs: false,
    name: 'MediaPicture',
  });

  const props = withDefaults(defineProps<Props>(), {
    alt: '',
    loading: 'lazy',
    fallback: 'fallback.jpg',
    multi: false,
  });

  const media = computed(() => {
    if (props.photo instanceof Array) {
      if (props.type) {
        return props.photo.filter((p) => p.type === props.type);
      } else {
        return props.photo;
      }
    }

    if (typeof props.photo === 'string') {
      return createPhotosFromFilename(props.photo);
    }

    if (props.model) {
      if (props.type) {
        return extractFromPhotoable(props.model, props.type, props.multi || false);
      } else {
        throw new Error('You must provide photo type when extracting photos from model!');
      }
    }

    if (props.photo) {
      return [props.photo];
    }

    return createPhotosFromFilename(props.fallback || 'fallback.jpg');
  });

  const photos = computed(() => {
    return media.value.map((photo) => {
      let sizes = parseSize(props.sizes).flatMap(({ size, density, min, max }) => [
        {
          srcset: photoUrl(photo.filename || props.fallback || 'fallback.webp', size, density, true),
          media: mediaQuery(min, max),
          type: 'image/webp',
        },
        {
          srcset: photoUrl(photo.filename || props.fallback || 'fallback.jpg', size, density, false),
          media: mediaQuery(min, max),
          type: photo.mime,
        },
      ]);

      let original = {
        srcset: photoUrl(photo.filename || props.fallback || 'fallback.webp', undefined, undefined, true),
        type: 'image/webp',
      };

      let image = {
        src: photoUrl(photo.filename || props.fallback || 'fallback.jpg', undefined, undefined),
        alt: photo.alt || props.alt || '',
        loading: props.loading || 'lazy',
      };

      return {
        sources: [...sizes, original],
        image: image,
      };
    });
  });
</script>
<template>
  <picture v-for="photo in photos" v-bind="$attrs">
    <source v-for="source in photo.sources" v-bind="source">
    <img v-bind="photo.image" />
  </picture>
</template>
